import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowLeft, Camera, Calendar, MapPin, User, Stack, ArrowSquareOut, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OutwardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewIdx, setPreviewIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/outward/${id}`);
        setRow(data);
      } catch (err) {
        toast.error("Outward entry not found");
        navigate("/outward");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this outward entry? This cannot be undone.")) return;
    try {
      await api.delete(`/outward/${id}`);
      toast.success("Deleted");
      navigate("/outward");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading || !row) {
    return <div className="p-10 text-center text-neutral-400">Loading…</div>;
  }

  const isBulk = row.type === "bulk";

  return (
    <div className="p-8 sm:p-10 max-w-[1200px] mx-auto" data-testid="outward-detail-page">
      <Link to="/outward" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-primary mb-6">
        <ArrowLeft size={14} /> Back to Outward
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-0.5 border ${isBulk ? "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" : "bg-[#F3E8FD] text-[#6B21A8] border-[#D8B4FE]"}`}>
              {isBulk ? <Stack size={11} /> : <ArrowSquareOut size={11} />} {row.type}
            </span>
            {row.flat_number && (
              <span className="font-mono-num text-sm bg-neutral-100 px-2 py-0.5 rounded-sm">{row.flat_number}</span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            {row.category}
            {row.subcategory && <span className="text-neutral-500 font-medium"> · {row.subcategory}</span>}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            {row.quantity} {row.unit} issued · {new Date(row.created_at).toLocaleString()}
          </p>
        </div>
        <Button variant="outline" className="rounded-sm gap-2 text-destructive hover:text-destructive" onClick={handleDelete} data-testid="delete-outward-btn">
          <Trash size={16} /> Delete
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetaBox icon={<Calendar size={16} />} label="Date" value={new Date(row.created_at).toLocaleDateString()} />
        <MetaBox icon={<MapPin size={16} />} label="Site" value={row.site_location || "—"} />
        <MetaBox icon={<User size={16} />} label="Issued To" value={row.issued_to || "—"} />
        <MetaBox icon={<User size={16} />} label="Recorded By" value={row.created_by_name || "—"} />
      </div>

      {row.notes && (
        <div className="bg-white border border-neutral-200 rounded-sm p-6 mb-4">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Notes</div>
          <p className="text-sm text-neutral-700 whitespace-pre-wrap">{row.notes}</p>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Camera size={16} className="text-primary" />
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Photo Trail</div>
          <span className="text-xs text-neutral-400 ml-auto">{row.photos?.length || 0} photos</span>
        </div>
        {row.photos?.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {row.photos.map((p, i) => (
              <PhotoTile key={i} photo={p} idx={i} onClick={() => setPreviewIdx(i)} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-neutral-400 py-8 text-center border border-dashed border-neutral-200 rounded-sm">
            No photos attached to this outward entry
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={previewIdx !== null} onOpenChange={(o) => !o && setPreviewIdx(null)}>
        <DialogContent className="max-w-4xl p-0 bg-neutral-950 border-neutral-800">
          {previewIdx !== null && (
            <PhotoLightbox photo={row.photos[previewIdx]} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaBox({ icon, label, value }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm px-4 py-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-sm font-medium text-neutral-900 truncate">{value}</div>
    </div>
  );
}

function PhotoTile({ photo, idx, onClick }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/files/${photo.storage_path}`, { credentials: "include" });
        if (!res.ok) throw new Error("Photo fetch failed");
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photo.storage_path]);

  return (
    <button
      onClick={onClick}
      className="relative aspect-square w-full overflow-hidden border border-neutral-200 rounded-sm bg-neutral-100 hover:opacity-90 transition-opacity"
      data-testid={`photo-tile-${idx}`}
    >
      {src ? (
        <img src={src} alt={photo.filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">Loading…</div>
      )}
    </button>
  );
}

function PhotoLightbox({ photo }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/files/${photo.storage_path}`, { credentials: "include" });
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photo.storage_path]);

  return (
    <div className="w-full">
      {src ? (
        <img src={src} alt={photo.filename} className="w-full h-auto max-h-[80vh] object-contain" />
      ) : (
        <div className="text-white text-center py-20">Loading…</div>
      )}
      <div className="text-white text-xs px-4 py-2 bg-black/60">{photo.filename}</div>
    </div>
  );
}
